import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedText } from "@/i18n/LocalizedText";
import { useEffect, useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Controller, useForm } from "react-hook-form";
import type { FieldError } from "react-hook-form";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check, Info, TriangleAlert, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { optionsPlatforms } from "@/Dashboard";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Brand } from "@/components/AppSidebar";
import { useOutletContext } from "react-router";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { canonicalizeDomain } from "@/utils/domain";
import { authAPI, brandsAPI } from "@/services/api";
import { countries } from "@/countries";
import {
  mapPlatformsToProviders,
  mapProvidersToPlatforms,
} from "@/utils/providers";
import { useQuery } from "@tanstack/react-query";
import IntegrationsPage from "./IntegrationsPage";

type FormValues = {
  option: string;
  brandName: string;
  brandWebsite: string;
  brandPlatforms: string[];
  brandCountry: string;
  currentFocus?: string;
};

const BrandPage = () => {
  const { t } = useLocale();
  const { currentBrand, setCurrentBrand } = useOutletContext<{
    currentBrand: Brand;
    setCurrentBrand: (brand: Brand) => void;
  }>();

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      option: "every24hours",
      brandName: currentBrand.name || "",
      brandWebsite: currentBrand.url || "",
      brandPlatforms: optionsPlatforms
        .filter((option) => !option.soon)
        .map((option) => option.value),
      brandCountry: currentBrand.country || "US",
      currentFocus: currentBrand.currentFocus || "",
    },
  });
  const { data: providerSettings } = useQuery({
    queryKey: ["provider-overrides", "settings"],
    queryFn: authAPI.getProviderOverrides,
  });
  const canEditProviderOverrides = !providerSettings?.isReadOnly;
  const hasLoadedProviderSettings = Boolean(providerSettings);
  const allowedProviderPlatforms = useMemo(
    () =>
      mapProvidersToPlatforms(providerSettings?.effectiveProviders || []) || [],
    [providerSettings?.effectiveProviders],
  );
  const allowedProviderSet = useMemo(
    () => new Set(allowedProviderPlatforms),
    [allowedProviderPlatforms],
  );

  useEffect(() => {
    setValue("brandName", currentBrand.name || "");
    setValue("brandWebsite", currentBrand.url || "");
    setValue("brandCountry", currentBrand.country || "US");
    setValue("currentFocus", currentBrand.currentFocus || "");
  }, [currentBrand, setValue]);

  useEffect(() => {
    const rawBrandProviderPlatforms =
      mapProvidersToPlatforms(currentBrand?.activeProviders || []) || [];
    const brandProviderPlatforms = hasLoadedProviderSettings
      ? rawBrandProviderPlatforms.filter((platform) =>
          allowedProviderSet.has(platform),
        )
      : rawBrandProviderPlatforms;
    const providerPlatforms = brandProviderPlatforms.length
      ? brandProviderPlatforms
      : allowedProviderPlatforms;
    if (!providerPlatforms.length) return;
    setValue("brandPlatforms", providerPlatforms, {
      shouldDirty: false,
    });
  }, [
    currentBrand?.activeProviders,
    hasLoadedProviderSettings,
    allowedProviderPlatforms,
    allowedProviderSet,
    setValue,
  ]);

  const onSubmit = async (data: FormValues) => {
    const brandId = currentBrand?.brandId || currentBrand?.id;
    if (!brandId) {
      toast.error(t("legacy.brand.not.found.1qzqou5"), {
        icon: (
          <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
            <TriangleAlert className="w-4 h-4 text-black" />
          </div>
        ),
        position: "bottom-right",
      });
      return;
    }
    if (canEditProviderOverrides && !data.brandPlatforms?.length) {
      toast.error(t("legacy.select.at.least.one.platform.vczi3w"), {
        icon: (
          <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
            <TriangleAlert className="w-4 h-4 text-black" />
          </div>
        ),
        position: "bottom-right",
      });
      return;
    }
    if (canEditProviderOverrides) {
      if (!hasLoadedProviderSettings) {
        toast.error(
          t("legacy.provider.settings.are.still.loading.try.again.xy45rg"),
          {
            icon: (
              <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
                <TriangleAlert className="w-4 h-4 text-black" />
              </div>
            ),
            position: "bottom-right",
          },
        );
        return;
      }
      const hasUnavailableSelection = (data.brandPlatforms || []).some(
        (platform) => !allowedProviderSet.has(platform),
      );
      if (hasUnavailableSelection) {
        toast.error(
          t("legacy.some.selected.platforms.are.not.available.for.your.7ym2mv"),
          {
            icon: (
              <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
                <TriangleAlert className="w-4 h-4 text-black" />
              </div>
            ),
            position: "bottom-right",
          },
        );
        return;
      }
    }
    try {
      const nextCountry = String(data.brandCountry || "")
        .trim()
        .toUpperCase();
      if (!nextCountry) {
        toast.error(t("legacy.primary.country.is.required.1rjw6lu"), {
          icon: (
            <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
              <TriangleAlert className="w-4 h-4 text-black" />
            </div>
          ),
          position: "bottom-right",
        });
        return;
      }
      const result = await brandsAPI.updateBrand(brandId, {
        country: nextCountry,
        currentFocus: (data.currentFocus || "").trim(),
        providers: canEditProviderOverrides
          ? mapPlatformsToProviders(data.brandPlatforms) || []
          : undefined,
      });
      const updatedCountry = String(result.brand.country || nextCountry)
        .trim()
        .toUpperCase();
      const updatedFocus = result.brand.currentFocus || "";
      setValue("brandCountry", updatedCountry, {
        shouldDirty: false,
      });
      setValue("currentFocus", result.brand.currentFocus || "", {
        shouldDirty: false,
      });
      setCurrentBrand({
        ...currentBrand,
        brandId,
        id: brandId,
        country: updatedCountry,
        currentFocus: updatedFocus,
        name: result.brand.name || currentBrand.name || "",
        url: result.brand.url || currentBrand.url || "",
        activeProviders: Array.isArray(result.brand.activeProviders)
          ? result.brand.activeProviders
          : currentBrand.activeProviders || null,
      });
      toast.success(t("legacy.the.changes.were.saved.ruhync"), {
        icon: (
          <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
            <Check className="w-4 h-4 text-black" />
          </div>
        ),
        position: "bottom-right",
      });
    } catch {
      toast.error(t("legacy.failed.to.save.changes.vz86x5"), {
        icon: (
          <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
            <TriangleAlert className="w-4 h-4 text-black" />
          </div>
        ),
        position: "bottom-right",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-5">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h1 className="text-base text-[#0A0A0A] font-semibold">
            <LocalizedText id="legacy.brand.info.1ucvhw0" />
          </h1>
          <div className="text-muted-foreground text-sm">
            <LocalizedText id="legacy.edit.your.brand.data.3s3zmr" />
          </div>
        </div>
        <div className="flex gap-4 flex-wrap">
          <div>
            <Label
              className="mb-2 text-sm font-medium text-foreground"
              htmlFor="brandName"
            >
              <LocalizedText id="legacy.brand.name.1udhmrd" />
            </Label>
            <Input
              disabled={true}
              id="brandName"
              type="email"
              autoComplete="off"
              {...register("brandName")}
              className="w-full font-normal text-muted-foreground text-sm px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-slate-300"
            />
            {errors.brandName && (
              <p className="text-red-500 text-sm mt-2">
                {(errors.brandName as FieldError)?.message}
              </p>
            )}
          </div>
          <div>
            <Label
              className="mb-2 text-sm font-medium text-foreground"
              htmlFor="brandWebsite"
            >
              <LocalizedText id="legacy.website.10ekgqm" />
            </Label>
            <Input
              disabled={true}
              id="brandWebsite"
              type="text"
              autoComplete="off"
              {...register("brandWebsite")}
              className="w-full font-normal text-muted-foreground text-sm px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-slate-300"
            />
            {errors.brandWebsite && (
              <p className="text-red-500 text-sm mt-2">
                {(errors.brandWebsite as FieldError)?.message}
              </p>
            )}
          </div>
          <div>
            <Label
              className="mb-2 text-sm font-medium text-foreground"
              htmlFor="brandLogo"
            >
              <LocalizedText id="legacy.brand.logo.1udghyt" />
            </Label>
            <div className="flex gap-2">
              <div className="bg-sidebar-primary-foreground text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                <img
                  className="w-8 rounded-md"
                  src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${canonicalizeDomain(currentBrand?.url) || ""}&size=64`}
                  alt="logo"
                />
              </div>
              <Button disabled={true} variant="outline">
                <Upload /> <LocalizedText id="legacy.upload.1clarza" />
              </Button>
            </div>
          </div>
        </div>
        <div>
          <div>
            <div className="flex text-sm gap-2 mb-2 items-center">
              <Label
                className="text-sm font-medium text-foreground"
                htmlFor="currentFocus"
              >
                <LocalizedText id="legacy.current.focus.11htrc0" />
              </Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info
                    size={16}
                    className="cursor-pointer text-[#737373]"
                  ></Info>
                </TooltipTrigger>
                <TooltipContent className="max-w-[300px] text-sm">
                  <p>
                    {" "}
                    <LocalizedText id="legacy.mention.the.offer.service.or.feature.who.it.lfqsks" />
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>

            <textarea
              placeholder={t(
                "legacy.example.ai.automation.for.accounting.firms.target.cfos.18t162",
              )}
              id="currentFocus"
              autoComplete="off"
              {...register("currentFocus")}
              className="w-full font-normal text-muted-foreground text-sm px-4 py-2 pb-8 border rounded-md focus:outline-none focus:ring-1 focus:ring-slate-300"
            />
            {errors.currentFocus && (
              <p className="text-red-500 text-sm mt-2">
                {(errors.currentFocus as FieldError)?.message}
              </p>
            )}
          </div>
        </div>
        <Separator className="mb-5" />
      </div>
      <div className="flex flex-col gap-6">
        <IntegrationsPage embedded />
        <Separator className="mb-5" />
      </div>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h1 className="text-base text-[#0A0A0A] font-semibold">
            <LocalizedText id="legacy.platforms.harnzd" />
          </h1>
          <div className="text-muted-foreground text-sm">
            <LocalizedText id="legacy.choose.which.ai.platforms.to.track.responses.from.sat0ib" />
          </div>
          {!canEditProviderOverrides && (
            <div className="text-xs text-muted-foreground">
              <LocalizedText id="legacy.provider.access.is.inherited.from.your.workspace.owner.1byngc3" />
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-4">
          <Controller
            name="brandPlatforms"
            control={control}
            render={({ field }) => (
              <>
                {optionsPlatforms.map((option) => (
                  <div
                    key={option.label}
                    className={`${option.soon && "opacity-50"} flex border ${field.value.includes(option.value) && "border-[#FF5E00] bg-[#FFE2CC]"}  p-3 rounded-md items-center space-x-3`}
                  >
                    <Label
                      htmlFor="platform"
                      className="flex items-center gap-2"
                    >
                      <div>{option.icon}</div>
                      {option.label}
                      {option.soon && <Badge variant="secondary">soon</Badge>}
                      <Checkbox
                        checked={field.value.includes(option.value)}
                        onCheckedChange={(checked) => {
                          const isChecked = Boolean(checked);
                          const newValue = isChecked
                            ? [...field.value, option.value]
                            : field.value.filter((v) => v !== option.value);

                          field.onChange(newValue);
                        }}
                        disabled={
                          option.soon ||
                          !canEditProviderOverrides ||
                          (hasLoadedProviderSettings &&
                            !allowedProviderSet.has(option.value))
                        }
                        className={`bg-white data-[state=checked]:bg-[#ff5e00] data-[state=checked]:border-0 data-[state=checked]:ring-0 data-[state=checked]:focus:ring-0 data-[state=checked]:focus-visible:ring-0 data-[state=indeterminate]:bg-[#ff5e00] data-[state=indeterminate]:border-0 data-[state=indeterminate]:ring-0 data-[state=indeterminate]:focus:ring-0 data-[state=indeterminate]:focus-visible:ring-0 data-[state=indeterminate]:text-white`}
                      />
                    </Label>
                  </div>
                ))}
              </>
            )}
          />
        </div>
        <Separator className="mb-5" />
      </div>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h1 className="text-base text-[#0A0A0A] font-semibold">
            <LocalizedText id="legacy.refresh.frequency.s1hr0" />
          </h1>
          <div className="text-muted-foreground text-sm">
            <LocalizedText id="legacy.choose.how.often.your.data.is.refreshed.38ygc9" />
          </div>
        </div>
        <div className="flex flex-wrap gap-4">
          <Controller
            name="option"
            control={control}
            render={({ field }) => (
              <RadioGroup
                value={field.value}
                onValueChange={field.onChange}
                className="flex flex-wrap space-y-1"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="every3days" id="every3days" />
                  <Label className="font-normal" htmlFor="every3days">
                    <LocalizedText id="legacy.every.3.days.sdbbd0" />
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="every24hours" id="every24hours" />
                  <Label className="font-normal" htmlFor="every24hours">
                    <LocalizedText id="legacy.every.24.hours.mw8q0d" />
                  </Label>
                </div>
                <div className="flex items-center space-x-2 opacity-50">
                  <RadioGroupItem disabled value="custom" id="Custom" />
                  <Label htmlFor="Custom">
                    <LocalizedText id="legacy.custom.1956hjq" />
                    <Badge variant="secondary">
                      <LocalizedText id="legacy.coming.soon.1eqsri1" />
                    </Badge>
                    <Input
                      className="font-normal"
                      disabled
                      value="00:00:00"
                    ></Input>
                  </Label>
                </div>
              </RadioGroup>
            )}
          />
        </div>
        <Separator className="mb-5" />
      </div>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h1 className="text-base text-[#0A0A0A] font-semibold">
            <LocalizedText id="legacy.primary.country.147f875" />
          </h1>
          <div className="text-muted-foreground text-sm">
            <LocalizedText id="legacy.break.down.visibility.by.markets.and.regions.xjqv1e" />
          </div>
        </div>
        <Controller
          name="brandCountry"
          control={control}
          rules={{ required: "Primary country is required" }}
          render={({ field }) => {
            const selectedCountry = countries.find(
              (country) => country.code === field.value,
            );
            return (
              <Select
                value={field.value}
                onValueChange={(value) => field.onChange(value)}
              >
                <SelectTrigger className="w-full max-w-[280px]">
                  {selectedCountry ? (
                    <div className="flex items-center gap-1">
                      {selectedCountry.icon}
                      <span>{selectedCountry.name}</span>
                    </div>
                  ) : (
                    <SelectValue
                      placeholder={t("legacy.select.country.fjx5mt")}
                    />
                  )}
                </SelectTrigger>
                <SelectContent className="max-h-[260px]">
                  {countries.map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      <div className="flex items-center gap-1">
                        {country.icon}
                        <span>{country.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          }}
        />
        {errors.brandCountry && (
          <p className="text-red-500 text-sm mt-2">
            {(errors.brandCountry as FieldError)?.message}
          </p>
        )}
        <Separator className="mb-5" />
      </div>
      <div className="w-full flex justify-center mt-5">
        <Button className="w-fit hover:bg-[#ff8138] hover:text-white text-white cursor-pointer bg-[#FF5E00]">
          <LocalizedText id="legacy.save.changes.p1tf81" />
        </Button>
      </div>
    </form>
  );
};

export default BrandPage;
